/**
 * Alt collection card add-to-cart: match theme product AJAX (btn--loading) + Rebuy Smart Cart
 * (same flow as product AjaxProduct + section-featured-collection-v2 Rebuy open).
 *
 * Do not require theme.js (theme.utils): this asset loads in the section before the deferred
 * theme.js at the layout footer, so theme.utils is undefined on first run and the form was never
 * wired — causing a full HTML POST to /cart/add and a redirect. Use FormData + cartAdd from the
 * inline head theme.routes instead (see section-featured-collection-v2.js).
 */
(function () {
  const FORM_SELECTOR = '.grid-product--alt .grid-product__atc-form';

  const cartAddUrl = () => {
    if (typeof theme !== 'undefined' && theme.routes && theme.routes.cartAdd) {
      return theme.routes.cartAdd;
    }
    return '/cart/add.js';
  };

  const openRebuyCart = () => {
    try {
      if (window.Rebuy?.SmartCart?.show) {
        window.Rebuy.SmartCart.show();
        window.Rebuy?.SmartCart?.reload?.();
        return true;
      }
      if (window.Rebuy?.Cart?.show) {
        window.Rebuy.Cart.show();
        window.Rebuy?.Cart?.reload?.();
        return true;
      }
      window.dispatchEvent(new CustomEvent('rebuy:cart.show'));
      window.dispatchEvent(new CustomEvent('rebuy:cart.reload'));
      const trigger =
        document.querySelector('[data-rebuy-open]') ||
        document.querySelector('.rebuy-cart__toggle') ||
        document.querySelector('[data-open-smartcart]');
      if (trigger) {
        trigger.click();
        return true;
      }
    } catch (e) {
      console.warn('[collection-grid-alt-atc] Rebuy open failed:', e);
    }
    return false;
  };

  /**
   * Rebuy’s script can load after this file; `rebuy:smartcart.ready` is the right hook
   * (https://developers.rebuyengine.com/reference/smart-cart-event-listeners/).
   */
  const openRebuyCartWhenReady = () => {
    if (openRebuyCart()) {
      return Promise.resolve(true);
    }
    return new Promise((resolve) => {
      let done = false;
      const finish = (ok) => {
        if (done) {
          return;
        }
        done = true;
        clearTimeout(timeoutId);
        document.removeEventListener('rebuy:smartcart.ready', onReady);
        resolve(ok);
      };

      const onReady = function () {
        finish(openRebuyCart());
      };

      const timeoutId = setTimeout(function () {
        finish(openRebuyCart());
      }, 2500);

      document.addEventListener('rebuy:smartcart.ready', onReady, { once: true });
    });
  };

  const wireForm = (form) => {
    if (form.dataset.collectionGridAltAtcWired === 'true') {
      return;
    }

    const btn = form.querySelector('.add-to-cart');
    if (!btn) {
      return;
    }

    if (typeof theme === 'undefined' || !theme.routes || !theme.routes.cartAdd) {
      return;
    }

    form.addEventListener('submit', (evt) => {
      evt.preventDefault();
      evt.stopPropagation();

      if (form.dataset.collectionGridAltAtcLoading === 'true') {
        return;
      }
      form.dataset.collectionGridAltAtcLoading = 'true';

      const wasDisabledBeforeLoad = btn.disabled;

      btn.classList.add('btn--loading');
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('disabled', 'true');

      const fd = new FormData(form);
      if (!fd.get('quantity')) {
        fd.set('quantity', '1');
      }

      fetch(cartAddUrl(), {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          if (!response.ok) {
            return response.json().then((err) => {
              throw err;
            });
          }
          return response.json();
        })
        .then((responseData) => {
          if (responseData && responseData.status === 422) {
            if (responseData.description) {
              window.alert(responseData.description);
            }
            return;
          }

          const product = responseData;

          document.dispatchEvent(
            new CustomEvent('ajaxProduct:added', {
              detail: {
                product: product,
                addToCartBtn: btn,
              },
            })
          );

          document.dispatchEvent(
            new CustomEvent('ajax:add-to-cart', { detail: product })
          );

          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'add_to_cart',
              ecommerce: {
                items: [
                  {
                    item_id: String(product?.id || ''),
                    item_name: product?.title || '',
                    quantity: Number(product?.quantity || 1),
                    price: product?.price != null ? product.price / 100 : undefined,
                  },
                ],
              },
            });
          }

          // Impulse cart drawer also listens on ajaxProduct:added; close it so Rebuy is primary.
          document.dispatchEvent(new CustomEvent('cart:close'));

          if (document.body.classList.contains('template-cart')) {
            window.scrollTo(0, 0);
            window.location.reload();
            return;
          }

          void openRebuyCartWhenReady().then((opened) => {
            if (!opened) {
              window.location.href = theme.routes?.cartPage || '/cart';
            }
          });
        })
        .catch((err) => {
          console.error('[collection-grid-alt-atc] Add to cart failed:', err);
          window.alert('We could not add this item. Please try again.');
        })
        .finally(() => {
          form.dataset.collectionGridAltAtcLoading = 'false';
          btn.classList.remove('btn--loading');
          btn.removeAttribute('aria-busy');
          if (wasDisabledBeforeLoad) {
            btn.setAttribute('disabled', 'true');
          } else {
            btn.removeAttribute('disabled');
          }
        });
    });

    form.dataset.collectionGridAltAtcWired = 'true';
  };

  const init = (root) => {
    const el = root && root.querySelectorAll ? root : document;
    el.querySelectorAll(FORM_SELECTOR).forEach(wireForm);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function onReady() {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('collection:reloaded', function () {
    init(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelectorAll) {
      init(event.target);
    } else {
      init(document);
    }
  });
})();
